import type {
  HealthApiKeyComparison,
  HealthProjectConfigStatus,
  HealthProjectRootComparison,
  HealthReportData,
} from "./index.js";

function renderValue(value: string | undefined, fallback = "not set"): string {
  return value?.trim() ? value : fallback;
}

function renderPresenceState(value: "set" | "missing" | "unknown"): string {
  switch (value) {
    case "set":
      return "set";
    case "missing":
      return "not set";
    case "unknown":
      return "unknown";
  }
}

function renderProjectRootComparison(
  comparison: HealthProjectRootComparison,
): string {
  switch (comparison) {
    case "same":
      return "same";
    case "different":
      return "different";
    case "input-only":
      return "only explicit input path is set";
    case "env-only":
      return "only PRIMEUI_PROJECT_ROOT is set";
    case "neither":
      return "neither explicit input path nor PRIMEUI_PROJECT_ROOT is set";
  }
}

function renderApiKeyComparison(comparison: HealthApiKeyComparison): string {
  switch (comparison) {
    case "same":
      return "same";
    case "different":
      return "different";
    case "not-comparable":
      return "not comparable";
  }
}

function renderConfigStatus(status: HealthProjectConfigStatus): string {
  switch (status) {
    case "valid":
      return "valid";
    case "missing":
      return "missing";
    case "invalid-json":
      return "invalid JSON";
    case "invalid-format":
      return "invalid format";
    case "read-failed":
      return "read failed";
    case "unresolved":
      return "unresolved";
  }
}

function formatSection(title: string, lines: string[]): string {
  return [title, ...lines.map((line) => `- ${line}`)].join("\n");
}

export function formatHealthReport(report: HealthReportData): string {
  const sections = [
    "PrimeUI MCP Health",
    "",
    formatSection("Version", [
      `current: ${report.version.currentVersion}`,
      `latest: ${renderValue(report.version.latestVersion, "unavailable")}`,
      `update status: ${report.version.updateStatus}${
        report.version.detail ? ` (${report.version.detail})` : ""
      }`,
    ]),
    "",
    formatSection("Runtime Context", [
      `cwd: ${report.runtimeContext.cwd}`,
      `health input path: ${renderValue(report.runtimeContext.inputProjectRoot)}`,
      `PRIMEUI_PROJECT_ROOT: ${renderValue(report.runtimeContext.envProjectRoot)}`,
      `PRIMEUI_API_BASE_URL: ${renderValue(report.runtimeContext.envApiBaseUrl)}`,
      `PRIMEUI_API_KEY: ${report.runtimeContext.envApiKeySet ? "set" : "not set"}`,
    ]),
    "",
    formatSection("Source Resolution", [
      `input project root: ${renderValue(report.sourceResolution.projectRoot.inputPath)}`,
      `env project root: ${renderValue(report.sourceResolution.projectRoot.envPath)}`,
      `project root comparison: ${renderProjectRootComparison(
        report.sourceResolution.projectRoot.comparison,
      )}`,
      `effective project root source: ${report.sourceResolution.projectRoot.effectiveSource}`,
      `env API key: ${renderPresenceState(report.sourceResolution.apiKey.envState)}`,
      `config API key: ${renderPresenceState(report.sourceResolution.apiKey.configState)}`,
      `API key comparison: ${renderApiKeyComparison(
        report.sourceResolution.apiKey.comparison,
      )}`,
      `effective API key source: ${report.sourceResolution.apiKey.effectiveSource}`,
    ]),
    "",
    formatSection("Project Resolution", [
      `resolved project root: ${renderValue(
        report.projectResolution.resolvedProjectRoot,
        "unresolved",
      )}`,
      `effective project root source: ${report.projectResolution.effectiveSource}`,
      `project root validation via explicit input: ${
        report.projectResolution.inputProjectRootValidated
          ? "passed"
          : "not used or failed"
      }`,
      `.primeui directory: ${
        report.projectResolution.primeUiDirStatus === "found"
          ? `found at ${report.projectResolution.primeUiDirPath}`
          : report.projectResolution.primeUiDirStatus
      }`,
      `config path: ${
        report.projectResolution.configPath
          ? `${report.projectResolution.configPath} (${renderConfigStatus(
              report.projectResolution.configStatus,
            )})`
          : renderConfigStatus(report.projectResolution.configStatus)
      }`,
      `targetProjectPath: ${report.projectResolution.targetProjectPath ?? "unresolved"}`,
      `resolved target project root: ${renderValue(
        report.projectResolution.targetProjectRoot,
        "unresolved",
      )}`,
      `project resolution detail: ${renderValue(
        report.projectResolution.errorMessage,
        "none",
      )}`,
    ]),
    "",
    formatSection("API Check", [
      `API root: ${renderValue(report.apiCheck.apiRoot, "unresolved")}`,
      `project info URL: ${renderValue(report.apiCheck.projectInfoUrl, "unresolved")}`,
      `PRIMEUI_API_BASE_URL source: ${report.apiCheck.apiBaseUrlSource}`,
      `non-standard API base URL: ${report.apiCheck.nonStandardBaseUrl ? "yes" : "no"}`,
      `project info request: ${report.apiCheck.status}`,
      `request detail: ${report.apiCheck.message}`,
    ]),
  ];

  if (report.nestedSearch.performed) {
    const candidateLines =
      report.nestedSearch.candidates.length > 0
        ? report.nestedSearch.candidates.map((candidate) => {
            const parts = [
              candidate.primeUiPath,
              `project root: ${candidate.projectRoot}`,
              `config: ${renderConfigStatus(candidate.configStatus)}`,
            ];
            if (candidate.targetProjectPath) {
              parts.push(`targetProjectPath: ${candidate.targetProjectPath}`);
            }
            return parts.join("; ");
          })
        : ["none found"];

    sections.splice(
      sections.length - 2,
      0,
      "",
      formatSection("Nested PrimeUI Candidates", [
        `search root: ${renderValue(report.nestedSearch.rootPath, "unresolved")}`,
        ...candidateLines,
      ]),
    );
  }

  sections.push("");
  if (report.hints.length === 0) {
    sections.push("Hints\n- none");
  } else {
    sections.push(
      [
        "Hints",
        ...report.hints.map((hint, index) => `${index + 1}. ${hint}`),
      ].join("\n"),
    );
  }

  return sections.join("\n");
}
